import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { PortalShell } from "@/components/layout/PortalShell";
import { STAFF_NAV } from "@/lib/staff-nav";
import { OperationsBanner } from "@/components/staff/OperationsBanner";
import { Eyebrow } from "@/components/brand/Eyebrow";
import { PrimaryButton } from "@/components/brand/PrimaryButton";
import { supabase } from "@/integrations/supabase/client";

interface Appt {
  id: string;
  scheduled_at: string;
  duration_minutes: number;
  status: string;
  notes: string | null;
  client_id: string | null;
}
interface Household { id: string; full_name: string | null; email: string | null }

export const Route = createFileRoute("/staff/appointments")({
  head: () => ({
    meta: [
      { title: "Scheduling — Operations" },
      { name: "description", content: "Book, reschedule, and cancel client appointments." },
      { property: "og:title", content: "Scheduling — Operations" },
      { property: "og:description", content: "Book, reschedule, and cancel client appointments." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: StaffScheduling,
});

function StaffScheduling() {
  const [rows, setRows] = useState<Appt[]>([]);
  const [households, setHouseholds] = useState<Household[]>([]);
  const [when, setWhen] = useState("");
  const [clientId, setClientId] = useState("");
  const [notes, setNotes] = useState("");

  const load = async () => {
    const [{ data: a }, { data: h }] = await Promise.all([
      supabase.from("appointments").select("id, scheduled_at, duration_minutes, status, notes, client_id").order("scheduled_at", { ascending: true }),
      supabase.from("staff_client_directory").select("id, full_name, email"),
    ]);
    setRows((a as Appt[]) ?? []);
    setHouseholds((h as Household[]) ?? []);
  };
  useEffect(() => { void load(); }, []);

  const create = async () => {
    if (!when) { toast.error("Pick a date and time"); return; }
    const { error } = await supabase.from("appointments").insert({
      scheduled_at: new Date(when).toISOString(),
      duration_minutes: 30,
      status: "scheduled",
      client_id: clientId || null,
      notes: notes || null,
    });
    if (error) { toast.error(error.message); return; }
    setWhen(""); setNotes(""); setClientId("");
    toast.success("Appointment scheduled");
    void load();
  };

  const setStatus = async (id: string, status: "scheduled" | "cancelled" | "completed") => {
    const { error } = await supabase.from("appointments").update({ status }).eq("id", id);
    if (error) { toast.error(error.message); return; }
    void load();
  };

  const fmt = (iso: string) =>
    new Date(iso).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" });

  return (
    <ProtectedRoute requiredRole="staff">
      <PortalShell title="Scheduling" nav={STAFF_NAV}>
        <OperationsBanner />
        <Eyebrow>Calendar</Eyebrow>
        <h1 className="mt-3 font-serif text-3xl font-semibold text-navy-900">Scheduling</h1>

        <div className="mt-6 grid gap-3 rounded-sm border border-bronze-600/30 bg-white p-4 sm:grid-cols-4 sm:items-end">
          <label className="font-sans text-[11px] uppercase tracking-[0.14em] text-charcoal-500">
            Date &amp; time
            <input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)}
              className="mt-1 block min-h-11 w-full rounded-sm border border-bronze-600/40 bg-cream-50 px-3 text-base text-charcoal-900" />
          </label>
          <label className="font-sans text-[11px] uppercase tracking-[0.14em] text-charcoal-500">
            Household
            <select value={clientId} onChange={(e) => setClientId(e.target.value)}
              className="mt-1 block min-h-11 w-full rounded-sm border border-bronze-600/40 bg-cream-50 px-2 text-base text-charcoal-900">
              <option value="">— none —</option>
              {households.map((h) => (<option key={h.id} value={h.id}>{h.full_name || h.email || h.id.slice(0, 8)}</option>))}
            </select>
          </label>
          <label className="font-sans text-[11px] uppercase tracking-[0.14em] text-charcoal-500">
            Notes
            <input value={notes} onChange={(e) => setNotes(e.target.value)}
              className="mt-1 block min-h-11 w-full rounded-sm border border-bronze-600/40 bg-cream-50 px-3 text-base text-charcoal-900" />
          </label>
          <PrimaryButton onClick={() => void create()}>Schedule</PrimaryButton>
        </div>

        <ul className="mt-6 divide-y divide-bronze-600/15 rounded-sm border border-bronze-600/30 bg-white">
          {rows.length === 0 && <li className="px-4 py-6 font-sans text-sm text-charcoal-500">No appointments.</li>}
          {rows.map((a) => (
            <li key={a.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
              <div>
                <p className="font-sans text-sm text-charcoal-900">{fmt(a.scheduled_at)} · {a.duration_minutes} min</p>
                <p className="font-sans text-[11px] uppercase tracking-[0.14em] text-charcoal-500">{a.status}{a.notes ? ` · ${a.notes}` : ""}</p>
              </div>
              {a.status === "scheduled" && (
                <button onClick={() => void setStatus(a.id, "cancelled")}
                  className="min-h-11 rounded-sm border border-bronze-600 px-4 font-sans text-[12px] font-semibold uppercase tracking-[0.08em] text-navy-800">
                  Cancel
                </button>
              )}
            </li>
          ))}
        </ul>
      </PortalShell>
    </ProtectedRoute>
  );
}
